// src/utils/deliveryValidator.js

/**
 * District suggestions for common misspellings
 */
const DISTRICT_SUGGESTIONS = {
  // Common typos - lowercase normalized
  'san isidro': 'San Isidro',
  'sanisidro': 'San Isidro',
  'milaflores': 'Miraflores',
  'miraflres': 'Miraflores',
  'chorillos': 'Chorrillos',
  'chorrilos': 'Chorrillos',
  'villa el salvador': 'Villa El Salvador',
  'villa salvador': 'Villa El Salvador',
  'san borja': 'San Borja',
  'sanborja': 'San Borja',
  'santiago de surco': 'Santiago de Surco',
  'surco': 'Santiago de Surco',
  'san juan de miraflores': 'San Juan de Miraflores',
  'sjm': 'San Juan de Miraflores',
  'la molina': 'La Molina',
  'lamolina': 'La Molina',
  'san martin de porres': 'San Martin de Porres',
  'smp': 'San Martin de Porres',
  'los olivos': 'Los Olivos',
  'losolivos': 'Los Olivos',
  'jesus maria': 'Jesús María',
  'magdalena del mar': 'Magdalena del Mar',
  'magdalena': 'Magdalena del Mar',
  'pueblo libre': 'Pueblo Libre',
  'san miguel': 'San Miguel',
  'sanmiguel': 'San Miguel',
  'el agustino': 'El Agustino',
  'la victoria': 'La Victoria',
  'san juan de lurigancho': 'San Juan de Lurigancho',
  'sjl': 'San Juan de Lurigancho',
  'ate': 'Ate',
  'santa anita': 'Santa Anita',
  'callao': 'Callao',
  'ventanilla': 'Ventanilla',
  'puente piedra': 'Puente Piedra',
  'carabayllo': 'Carabayllo',
  'comas': 'Comas',
  'independencia': 'Independencia',
  'rimac': 'Rimac',
  'barranco': 'Barranco',
  'surquillo': 'Surquillo',
  'lince': 'Lince',
  'breña': 'Breña',
  'cercado de lima': 'Cercado de Lima',
  'lima': 'Cercado de Lima'
};

/**
 * Validate a single delivery row
 * @param {Object} row - Row data from Excel
 * @param {number} rowNumber - Excel row number (for error reporting)
 * @param {Object} validDistricts - Map of valid districts from backend
 * @returns {Object} - Validation result
 */
export function validateDelivery(row, rowNumber, validDistricts = {}) {
  const errors = [];
  const warnings = [];
  const suggestions = {};
  
  // ===== REQUIRED FIELDS =====
  
  // Nombre y Apellido
  const name = row['Nombre y Apellido']?.toString().trim() || '';
  if (!name) {
    errors.push({
      field: 'name',
      message: 'Nombre y Apellido es obligatorio',
      severity: 'error'
    });
  } else if (name.length < 3) {
    warnings.push({
      field: 'name',
      message: 'Nombre parece muy corto',
      severity: 'warning'
    });
  }
  
  // WhatsApp
  const phone = row['WhatsApp']?.toString().trim() || '';
  if (!phone) {
    errors.push({
      field: 'phone',
      message: 'WhatsApp es obligatorio',
      severity: 'error'
    });
  } else {
    // Remove any non-digit characters
    const cleanPhone = phone.replace(/\D/g, '');
    
    if (cleanPhone.length !== 9) {
      errors.push({
        field: 'phone',
        message: `WhatsApp debe tener exactamente 9 dígitos (tiene ${cleanPhone.length})`,
        severity: 'error',
        currentValue: phone,
        suggestedValue: cleanPhone
      });
    } else if (!/^9\d{8}$/.test(cleanPhone)) {
      warnings.push({
        field: 'phone',
        message: 'WhatsApp debería empezar con 9 en Perú',
        severity: 'warning'
      });
    }
  }
  
  // Distrito
  const district = row['Distrito']?.toString().trim() || '';
  if (!district) {
    errors.push({
      field: 'district',
      message: 'Distrito es obligatorio',
      severity: 'error'
    });
  } else {
    // Get valid district list from backend response
    // Priority: all_districts array > district_driver_map keys > fallback to validDistricts keys
    let validDistrictList = [];
    
    if (Array.isArray(validDistricts)) {
      // If validDistricts is already an array (e.g., from district_driver_map keys)
      validDistrictList = validDistricts;
    } else if (validDistricts.all_districts && Array.isArray(validDistricts.all_districts)) {
      // Best case: backend provided all_districts array
      validDistrictList = validDistricts.all_districts;
    } else if (validDistricts.district_driver_map && typeof validDistricts.district_driver_map === 'object') {
      // Fallback: extract from district_driver_map
      validDistrictList = Object.keys(validDistricts.district_driver_map);
    } else {
      // Last resort: use validDistricts keys (for backward compatibility)
      validDistrictList = Object.keys(validDistricts);
    }
    
    // Check if district is valid (exact match, case-sensitive)
    const isValid = validDistrictList.includes(district);
    
    if (!isValid) {
      // Try fuzzy matching for typos
      const normalized = district.toLowerCase().trim();
      const suggestion = DISTRICT_SUGGESTIONS[normalized];
      
      if (suggestion) {
        // Only show warning if the suggestion is DIFFERENT from input
        // This prevents "San Isidro" → suggesting "San Isidro"
        if (suggestion !== district) {
          suggestions.district = suggestion;
          warnings.push({
            field: 'district',
            message: `Distrito "${district}" no reconocido`,
            severity: 'warning',
            suggestion: suggestion,
            autoFixable: true
          });
        } else {
          // Suggestion is same as input, means district is actually valid
          // but wasn't in validDistrictList - this shouldn't happen
          // but we'll treat it as valid to avoid confusing users
          console.warn(`District "${district}" matched fuzzy but not in validDistrictList`, validDistrictList);
        }
      } else {
        // No fuzzy match found and not in valid list
        errors.push({
          field: 'district',
          message: `Distrito "${district}" no tiene conductor asignado`,
          severity: 'error',
          showDistrictPicker: true
        });
      }
    }
  }
  
  // Dirección
  const address = row['Direccion']?.toString().trim() || '';
  if (!address) {
    errors.push({
      field: 'address',
      message: 'Dirección es obligatoria',
      severity: 'error'
    });
  } else if (address.length < 10) {
    warnings.push({
      field: 'address',
      message: 'Dirección parece muy corta',
      severity: 'warning'
    });
  }
  
  // SKU or Producto (at least one required)
  const sku = row['SKU']?.toString().trim() || '';
  const product = row['Producto']?.toString().trim() || '';
  
  if (!sku && !product) {
    errors.push({
      field: 'product',
      message: 'Debe proporcionar SKU o Producto (al menos uno)',
      severity: 'error'
    });
  }
  
  // ===== OPTIONAL FIELDS =====
  
  // Cobrar (if provided, must be numeric)
  const charge = row['Cobrar']?.toString().trim() || '0';
  if (charge) {
    const chargeNum = parseFloat(charge);
    if (isNaN(chargeNum)) {
      warnings.push({
        field: 'charge',
        message: 'Monto a cobrar debe ser numérico',
        severity: 'warning',
        currentValue: charge,
        suggestedValue: '0'
      });
    } else if (chargeNum < 0) {
      warnings.push({
        field: 'charge',
        message: 'Monto a cobrar no puede ser negativo',
        severity: 'warning'
      });
    }
  }
  
  // Cantidad (if provided, must be positive integer)
  const quantity = row['Cantidad']?.toString().trim() || '1';
  if (quantity) {
    const quantityNum = parseInt(quantity);
    if (isNaN(quantityNum)) {
      warnings.push({
        field: 'quantity',
        message: 'Cantidad debe ser un número entero',
        severity: 'warning',
        currentValue: quantity,
        suggestedValue: '1'
      });
    } else if (quantityNum < 1) {
      warnings.push({
        field: 'quantity',
        message: 'Cantidad debe ser al menos 1',
        severity: 'warning',
        suggestedValue: '1'
      });
    }
  }
  
  // Notas (no validation, just sanitize)
  const notes = row['Notas']?.toString().trim() || '';
  
  return {
    rowNumber,
    isValid: errors.length === 0,
    hasWarnings: warnings.length > 0,
    canAutoFix: warnings.some(w => w.autoFixable),
    errors,
    warnings,
    suggestions,
    data: {
      'Nombre y Apellido': name,
      'WhatsApp': phone.replace(/\D/g, ''), // Clean phone
      'Distrito': suggestions.district || district,
      'Direccion': address,
      'Producto': product,
      'SKU': sku,
      'Cantidad': quantity || '1',
      'Cobrar': charge || '0',
      'Notas': notes
    }
  };
}

/**
 * Validate entire array of deliveries
 * @param {Array} rows - Array of row objects from Excel
 * @param {Object} validDistricts - Map of valid districts
 * @returns {Object} - Validation summary
 */
export function validateAllDeliveries(rows, validDistricts) {
  const validatedRows = rows.map((row, index) => 
    validateDelivery(row, index + 2, validDistricts) // +2 because Excel row 1 is header
  );
  
  const validCount = validatedRows.filter(r => r.isValid).length;
  const errorCount = validatedRows.filter(r => !r.isValid).length;
  const warningCount = validatedRows.filter(r => r.hasWarnings && r.isValid).length;
  
  return {
    rows: validatedRows,
    summary: {
      total: rows.length,
      valid: validCount,
      errors: errorCount,
      warnings: warningCount,
      canProceed: errorCount === 0
    }
  };
}

/**
 * Auto-fix warnings where possible
 * @param {Object} validatedRow - Validated row object
 * @returns {Object} - Updated row with fixes applied
 */
export function autoFixRow(validatedRow) {
  const fixed = { ...validatedRow };
  
  // Apply suggestions
  if (fixed.suggestions.district) {
    fixed.data['Distrito'] = fixed.suggestions.district;
  }
  
  // Clean phone number
  const phone = fixed.data['WhatsApp'];
  if (phone) {
    fixed.data['WhatsApp'] = phone.replace(/\D/g, '');
  }
  
  // Fix numeric fields
  fixed.warnings.forEach(warning => {
    if (warning.suggestedValue) {
      fixed.data[warning.field] = warning.suggestedValue;
    }
  });
  
  return fixed;
}